/**
 * Auth-Store (leichtgewichtig, ohne zusaetzliche Dependency) — haelt den
 * Token-Provider sowie Re-Login-/Logout-Handler ausserhalb von React bereit,
 * damit der Axios-Interceptor (src/lib/api.ts) unabhaengig vom Render-Baum
 * darauf zugreifen kann. Muster: rmnc-personal/frontend/src/store/auth.ts,
 * hier auf das Minimum reduziert, das rmnc-technik braucht.
 */

export const AUTH_MODE: "dev" | "logto" | "authentik" =
  import.meta.env.VITE_AUTH_MODE === "dev"
    ? "dev"
    : import.meta.env.VITE_AUTH_MODE === "authentik"
      ? "authentik"
      : "logto"

// --- Token-Provider (vom LogtoAuth-Gate registriert) ------------------------

type TokenProvider = () => Promise<string | null>

let _tokenProvider: TokenProvider | null = null

export function setTokenProvider(provider: TokenProvider | null): void {
  _tokenProvider = provider
}

export async function getAccessToken(): Promise<string | null> {
  if (!_tokenProvider) return null
  try {
    return await _tokenProvider()
  } catch {
    return null
  }
}

// --- Zweiter Token-Provider: Nachrichten-Service (rmnc-nachrichten) ----------
// Eigene Logto-API-Resource (VITE_LOGTO_NACHRICHTEN_RESOURCE) → eigenes
// Access-Token mit aud=nachrichten-api; registriert vom LogtoAuth-Gate.

let _nachrichtenTokenProvider: TokenProvider | null = null

export function setNachrichtenTokenProvider(provider: TokenProvider | null): void {
  _nachrichtenTokenProvider = provider
}

export async function getNachrichtenAccessToken(): Promise<string | null> {
  if (!_nachrichtenTokenProvider) return null
  try {
    return await _nachrichtenTokenProvider()
  } catch {
    return null
  }
}

// --- Re-Login / Logout (vom LogtoAuth-Gate registriert) ---------------------

type AuthActionHandler = () => void | Promise<void>

let _reloginHandler: AuthActionHandler | null = null
let _logoutHandler: AuthActionHandler | null = null
let _reloginAttempted = false

export function setReloginHandler(handler: AuthActionHandler | null): void {
  _reloginHandler = handler
}

export function setLogoutHandler(handler: AuthActionHandler | null): void {
  _logoutHandler = handler
}

/**
 * 401 vom Backend (abgelaufene Session): genau EIN Re-Login-Versuch pro
 * Seitenaufruf — der signIn-Redirect verlaesst die Seite ohnehin; die Sperre
 * verhindert Redirect-Schleifen, falls der Login selbst 401 produziert.
 */
export function triggerRelogin(): void {
  if (AUTH_MODE === "dev") return
  if (_reloginAttempted || !_reloginHandler) return
  _reloginAttempted = true
  void _reloginHandler()
}

export async function performLogout(): Promise<void> {
  if (AUTH_MODE === "logto" && _logoutHandler) {
    await _logoutHandler()
  }
}
